import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import Pricing from '../components/sections/Pricing';
import { HelpCircle, Mail, Phone, MessageCircle } from 'lucide-react';

// FAQ Items
const faqs = [
  {
    question: 'Kann ich Saldify kostenlos testen?',
    answer: 'Ja! Sie können Saldify 14 Tage lang kostenlos und unverbindlich testen. Danach können Sie den kostenlosen Starter-Tarif nutzen oder auf einen kostenpflichtigen Tarif upgraden.',
  },
  {
    question: 'Kann ich den Tarif jederzeit wechseln?',
    answer: 'Ja, Sie können jederzeit in einen höheren Tarif wechseln. Der Wechsel wird sofort aktiv. Bei einem Downgrade wird der neue Tarif zum nächsten Abrechnungszeitraum aktiv.',
  },
  {
    question: 'Gibt es eine Mindestvertragslaufzeit?',
    answer: 'Nein, alle Tarife sind monatlich kündbar. Bei jährlicher Zahlung sparen Sie 2 Monate, können aber auch hier jederzeit zum Ende der Laufzeit kündigen.',
  },
  {
    question: 'Was passiert mit meinen Daten bei Kündigung?',
    answer: 'Sie können Ihre Daten jederzeit exportieren. Nach Kündigung haben Sie noch 30 Tage Zugriff auf Ihre Daten. Danach werden sie gemäß DSGVO gelöscht – auf Wunsch auch sofort.',
  },
  {
    question: 'Bieten Sie Rabatte für gemeinnützige Organisationen?',
    answer: 'Ja! Gemeinnützige Organisationen, Vereine und Bildungseinrichtungen erhalten 50% Rabatt auf alle Tarife. Kontaktieren Sie uns für mehr Informationen.',
  },
  {
    question: 'Kann ich mehrere Unternehmen verwalten?',
    answer: 'Ja, im Business-Tarif können Sie unbegrenzt viele Mandanten (Unternehmen) in einem Account verwalten. Für jeden Mandanten gibt es separate Daten und Auswertungen.',
  },
  {
    question: 'Welche Zahlungsmethoden akzeptieren Sie?',
    answer: 'Wir akzeptieren Kreditkarten (Visa, Mastercard, American Express), PayPal, SEPA-Lastschrift und Überweisung. Für Business-Kunden bieten wir auch Rechnungskauf an.',
  },
  {
    question: 'Ist meine Buchhaltung DSGVO-konform?',
    answer: 'Ja, Saldify ist vollständig DSGVO-konform. Alle Daten werden auf Servern in Deutschland gespeichert und nach höchsten Sicherheitsstandards geschützt.',
  },
];

export default function PreisePage(): ReactElement {
  return (
    <div className="pt-20">
      {/* Pricing Section (imported) */}
      <Pricing />

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-primary-600 font-semibold text-sm tracking-wider uppercase mb-4">
              FAQ
            </span>
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Häufig gestellte Fragen
            </h2>
            <p className="text-lg text-secondary-600">
              Alles, was Sie über unsere Preise und Tarife wissen müssen.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-secondary-50 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-medium text-secondary-900 pr-4">
                    {faq.question}
                  </h3>
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center group-open:bg-primary-600 transition-colors">
                    <svg
                      className="w-4 h-4 text-primary-600 group-open:text-white transition-transform group-open:rotate-45"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-secondary-600">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise / Custom Section */}
      <section className="py-16 lg:py-24 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-secondary-900 to-secondary-800 rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Individuelle Lösung für Ihr Unternehmen?
            </h2>
            <p className="text-lg text-secondary-300 mb-8 max-w-2xl mx-auto">
              Für größere Unternehmen, spezielle Anforderungen oder On-Premise-Installation
              erstellen wir gerne ein individuelles Angebot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#kontakt"
                className="bg-white text-secondary-900 hover:bg-secondary-100 px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Beratungsgespräch vereinbaren
              </a>
              <a
                href="mailto:enterprise@saldify.de"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                enterprise@saldify.de
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Noch Fragen?
            </h2>
            <p className="text-lg text-secondary-600">
              Unser Team hilft Ihnen gerne bei der Auswahl des richtigen Tarifs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">Live-Chat</h3>
              <p className="text-secondary-600 text-sm mb-4">
                Mo-Fr von 9-18 Uhr
              </p>
              <button className="text-primary-600 font-medium hover:text-primary-700">
                Chat starten →
              </button>
            </div>

            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">E-Mail</h3>
              <p className="text-secondary-600 text-sm mb-4">
                Antwort innerhalb von 24h
              </p>
              <a href="mailto:support@saldify.de" className="text-primary-600 font-medium hover:text-primary-700">
                support@saldify.de →
              </a>
            </div>

            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">Hilfe-Center</h3>
              <p className="text-secondary-600 text-sm mb-4">
                Anleitungen & Tutorials
              </p>
              <Link to="/hilfe" className="text-primary-600 font-medium hover:text-primary-700">
                Zum Hilfe-Center →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
